import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-16196 — what kind of sensor alert email went out. `alerts` is sent when a
 * problem is new or at its temperature reminder; `daily_summary` is the 7:00 AM
 * list of everything still open.
 */
export enum SensorAlertEmailKind {
	ALERTS = 'alerts',
	DAILY_SUMMARY = 'daily_summary',
}

/**
 * PD-16196 — one sensor alert email that was sent, and what it contained.
 *
 * Sensor alerts are consolidated into at most one email per health-cron run
 * (see apps/crm-api/src/modules/sensors/utils/alert-email-plan.ts). Deciding
 * what is due needs to know when the previous email and the previous daily
 * summary went out, and which resolved problems were already reported. Those
 * cannot be recovered reliably from the alerts' own timestamps (a new-problem
 * email at 7:05 AM would look like today's summary), and email_record carries
 * no link to alerts, so each sent email is recorded here. The row doubles as a
 * record of exactly what each recipient was told.
 *
 * Written only after the send succeeds, so a failed send is retried by the
 * next run. Not audited: a send log has no business edit history.
 */
@Index('sensor_alert_email_kind_created_idx', ['kind', 'createdDate'])
@Entity('sensor_alert_email', {}, { auditable: false })
export class SensorAlertEmailEntity extends BaseEntity {
	@Column('varchar', { name: 'kind', length: 20 })
	public kind!: SensorAlertEmailKind;

	@Column('int', { name: 'recipientCount', unsigned: true })
	public recipientCount!: number;

	/** Alerts reported for the first time. */
	@Column('json', { name: 'newAlertIds' })
	public newAlertIds!: Array<number>;

	/** Temperature alerts at their four-hour reminder. */
	@Column('json', { name: 'escalatedAlertIds' })
	public escalatedAlertIds!: Array<number>;

	/** Other open alerts listed for completeness. */
	@Column('json', { name: 'stillOpenAlertIds' })
	public stillOpenAlertIds!: Array<number>;

	/** Previously reported alerts that had ended. */
	@Column('json', { name: 'resolvedAlertIds' })
	public resolvedAlertIds!: Array<number>;
}
